import React from 'react';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { isSameDay, isDateInRange, isToday } from '../utils/dateHelpers';

function Calendar({
  currentMonth,
  onMonthChange,
  startDate,
  endDate,
  onDateSelect,
  minDate = new Date(),
  maxDate = new Date(2030, 11, 31),
  hoveredDate = null,
  onDateHover,
  showPrevChevron = true,
  showNextChevron = true,
  weekStartsOn = 1,
}) {
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  // Leading-blank count: how many cells before the 1st belong to the prior month
  // given the user's chosen first day (0 = Sun, 1 = Mon).
  const firstDayOfWeek = (firstDayOfMonth.getDay() - weekStartsOn + 7) % 7;
  const daysInMonth = lastDayOfMonth.getDate();

  const calendarDays = [];

  for (let i = 0; i < firstDayOfWeek; i++) {
    const prevDate = new Date(firstDayOfMonth);
    prevDate.setDate(prevDate.getDate() - (firstDayOfWeek - i));
    calendarDays.push({ date: prevDate, isCurrentMonth: false });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    calendarDays.push({ date, isCurrentMonth: true });
  }

  const remainingCells = 42 - calendarDays.length;
  for (let day = 1; day <= remainingCells; day++) {
    const nextDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, day);
    calendarDays.push({ date: nextDate, isCurrentMonth: false });
  }

  const handlePrevMonth = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    onMonthChange(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    onMonthChange(nextMonth);
  };

  // Normalise minDate to start-of-day so today's date isn't accidentally
  // disabled in the afternoon.
  const minDateStart = new Date(minDate);
  minDateStart.setHours(0, 0, 0, 0);

  const isDisabled = (date) => date < minDateStart || date > maxDate;

  const getDayClasses = (dayInfo) => {
    const { date, isCurrentMonth } = dayInfo;
    const disabled = isDisabled(date);

    const classes = [
      'w-14 h-14 sm:w-12 sm:h-12 flex items-center justify-center text-base sm:text-sm font-medium transition-colors duration-150',
      'touch-manipulation select-none',
      'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1',
    ];

    // Range membership including hover preview when end is not yet set.
    const previewEnd = endDate || (startDate && hoveredDate && hoveredDate >= startDate ? hoveredDate : null);
    const isInRange = startDate && previewEnd && isDateInRange(date, startDate, previewEnd);
    const isStartDate = startDate && isSameDay(date, startDate);
    const isEndDate = previewEnd && isSameDay(date, previewEnd);
    const isOnlyStart = isStartDate && !endDate && !previewEnd;
    const isPreview = !endDate && isInRange && !isStartDate;

    // Cursor + base text colour
    if (disabled) {
      classes.push('cursor-not-allowed');
    } else {
      classes.push('cursor-pointer');
    }

    if (!isCurrentMonth) {
      // Faded but still selectable (clicking jumps to that month + selects).
      classes.push(disabled ? 'text-text-muted/30' : 'text-text-muted/60');
    } else if (disabled) {
      // Past / over-max within current month - clearly disabled.
      classes.push('text-text-muted/40 line-through');
    } else if (isInRange || isOnlyStart) {
      classes.push('text-text-secondary');
    } else {
      classes.push('text-text-secondary hover:bg-surface');
    }

    // Rounding: pill ends for start/end, square for middle of range.
    if (isInRange && !isStartDate && !isEndDate) {
      classes.push('rounded-none');
    } else if (isStartDate && isEndDate) {
      classes.push('rounded-lg');
    } else if (isStartDate && isInRange) {
      classes.push('rounded-l-lg rounded-r-none');
    } else if (isEndDate && isInRange) {
      classes.push('rounded-r-lg rounded-l-none');
    } else {
      classes.push('rounded-lg');
    }

    // Range fills
    if (isStartDate || isEndDate || isOnlyStart) {
      classes.push('!bg-accent !text-white hover:!bg-interactive-hover');
    } else if (isInRange) {
      classes.push(isPreview ? '!bg-subtle/60 !text-accent' : '!bg-subtle !text-accent');
    }

    // Today indicator - a subtle inset dot that survives selection.
    if (isToday(date) && isCurrentMonth) {
      classes.push('relative after:content-[""] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full');
      // Dot colour flips so it stays visible on the accent fill.
      if (isStartDate || isEndDate || isOnlyStart) {
        classes.push('after:bg-white');
      } else {
        classes.push('after:bg-accent');
      }
    }

    return classes.join(' ');
  };

  const handleDateClick = (dayInfo) => {
    const { date, isCurrentMonth } = dayInfo;

    if (isDisabled(date)) return;

    // Adjacent-month dates jump the visible month AND select.
    if (!isCurrentMonth) {
      const targetMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      onMonthChange(targetMonth);
    }

    onDateSelect(date);
  };

  return (
    <div className="bg-card">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        {showPrevChevron ? (
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrevMonth}
            className="p-3 hover:bg-surface rounded-lg transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Previous month"
          >
            <SafeIcon icon={FiChevronLeft} className="w-5 h-5 text-text-secondary" />
          </motion.button>
        ) : (
          <div className="w-11 h-11" />
        )}

        <h3 className="text-base sm:text-lg font-bold text-text-primary px-2 text-center">
          {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
        </h3>

        {showNextChevron ? (
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNextMonth}
            className="p-3 hover:bg-surface rounded-lg transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Next month"
          >
            <SafeIcon icon={FiChevronRight} className="w-5 h-5 text-text-secondary" />
          </motion.button>
        ) : (
          <div className="w-11 h-11" />
        )}
      </div>

      <div className="grid grid-cols-7 gap-0 mb-2">
        {(weekStartsOn === 0
          ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
          : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
        ).map(day => (
          <div key={day} className="h-12 sm:h-12 flex items-center justify-center">
            <span className="text-xs font-semibold text-text-muted uppercase tracking-wide">{day}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0">
        {calendarDays.map((dayInfo, index) => {
          const disabled = isDisabled(dayInfo.date);
          return (
            <button
              type="button"
              key={`${dayInfo.date.getTime()}-${index}`}
              className={getDayClasses(dayInfo)}
              onClick={() => handleDateClick(dayInfo)}
              onMouseEnter={() => !disabled && onDateHover && onDateHover(dayInfo.date)}
              onMouseLeave={() => onDateHover && onDateHover(null)}
              disabled={disabled}
              aria-label={`${dayInfo.date.toLocaleDateString('en-GB', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}${disabled ? ' (unavailable)' : ''}`}
              tabIndex={disabled ? -1 : 0}
            >
              <span className="pointer-events-none">{dayInfo.date.getDate()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default Calendar;
